/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useState } from "react";
import { PopoutScreenProps } from "../Options/Options";
import {
  Button,
  Divider,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import { Backspace, Close, Save } from "@mui/icons-material";
import { DisplayController } from "RosterData";
import SettingsToggle from "./SettingsToggle";
import { useMutation } from "@tanstack/react-query";
import { savePreferences } from "../../api";
import { LoadingButton } from "@mui/lab";
import { AxiosError } from "axios";

const SettingsScreen = (props: PopoutScreenProps) => {
  const [existingSettings, setExistingSettings] = useState<DisplayController>(
    props.initialData
  );
  const handleClose = () => {
    props.onClose(null);
  };

  const saveSettingsToPreference = useMutation({
    mutationFn: (settings: DisplayController) => {
      return savePreferences(settings);
    },
    onSuccess: () => {
      props.setInitialData(existingSettings);
      props.getLatestReportData();
      props.onClose(null);
    },
    onError: (err: AxiosError) => props.handleError(err),
  });

  const handleSaveClicked = async () => {
    saveSettingsToPreference.mutate(existingSettings);
  };

  const handleClear = () => {
    setExistingSettings({
      showPersonnelId: false,
      showJobTitle: false,
      showEmployer: false,
      showCoreCrew: false,
      showGender: false,
      showDiscipline: false,
      showTeam: false,
      showDeptCode: false,
      showTraffic: false,
      showTotals: false,
    });
  };

  return (
    <Popover
      anchorEl={props.anchorEl}
      open={Boolean(props.anchorEl)}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <div
        style={{
          padding: 10,
          width: 490,
        }}
      >
        <IconButton onClick={handleClose}>
          <Close fontSize="inherit" />
        </IconButton>
        <Divider>
          <Typography variant="h6">Display</Typography>
        </Divider>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <SettingsToggle
            displayName="Display Vantage Id"
            name="showPersonnelId"
            settings={existingSettings}
            setter={setExistingSettings}
          />
          <SettingsToggle
            displayName="Display Gender"
            name="showGender"
            settings={existingSettings}
            setter={setExistingSettings}
          />
          <SettingsToggle
            displayName="Display Employer"
            name="showEmployer"
            settings={existingSettings}
            setter={setExistingSettings}
          />
          <SettingsToggle
            displayName="Display Discipline"
            name="showDiscipline"
            settings={existingSettings}
            setter={setExistingSettings}
          />
          <SettingsToggle
            displayName="Display Job Title"
            name="showJobTitle"
            settings={existingSettings}
            setter={setExistingSettings}
          />
          <SettingsToggle
            displayName="Display Core Crew"
            name="showCoreCrew"
            settings={existingSettings}
            setter={setExistingSettings}
          />
          <SettingsToggle
            displayName="Display Team"
            name="showTeam"
            settings={existingSettings}
            setter={setExistingSettings}
          />
          <SettingsToggle
            displayName="Display Department"
            name="showDeptCode"
            settings={existingSettings}
            setter={setExistingSettings}
          />
        </div>
        <Divider>
          <Typography variant="h6">Summary</Typography>
        </Divider>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <SettingsToggle
            displayName="Show Totals"
            name="showTotals"
            settings={existingSettings}
            setter={setExistingSettings}
          />
          <SettingsToggle
            displayName="Daily Trafic Summary"
            name="showTraffic"
            settings={existingSettings}
            setter={setExistingSettings}
          />
        </div>
        <div
          style={{
            display: "flex",
            marginTop: 10,
            justifyContent: "center",
          }}
        >
          <LoadingButton
            sx={{ mr: 1 }}
            variant="contained"
            size="small"
            loading={saveSettingsToPreference.isLoading}
            startIcon={<Save />}
            onClick={handleSaveClicked}
          >
            Save
          </LoadingButton>
          <Button
            variant="outlined"
            size="small"
            endIcon={<Backspace />}
            onClick={handleClear}
          >
            Clear All
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export default SettingsScreen;
